import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import { useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import { getMm } from "../theme/modernMaterialEnterprise";

/**
 * Shown when prova-på trial has ended and the member must choose a payment plan.
 */
export default function MembershipPaymentRequiredScreen({
  paymentUrl,
  trialEndsAt = null,
  redirecting = false,
}) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx } = useSystemButtonStyles();

  useEffect(() => {
    if (redirecting && paymentUrl) {
      window.location.assign(paymentUrl);
    }
  }, [redirecting, paymentUrl]);

  const handlePay = () => {
    if (paymentUrl) {
      window.location.href = paymentUrl;
    }
  };

  if (redirecting) {
    return (
      <Box sx={{ textAlign: "center", px: 2 }}>
        <CircularProgress sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
        <Typography
          sx={{
            mt: 2,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            color: getMm(isDarkMode).onSurfaceVariant,
          }}
        >
          {t("membership_payment_redirecting")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          bgcolor: getMm(isDarkMode).surfaceLowest,
          textAlign: "center",
        }}
      >
        <PaymentIcon sx={{ fontSize: 48, color: getMm(isDarkMode).primary, mb: 2 }} />
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: "1.375rem",
            mb: 1,
            color: getMm(isDarkMode).onSurface,
          }}
        >
          {t("membership_payment_required_title")}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            color: getMm(isDarkMode).onSurfaceVariant,
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          {t("membership_payment_required_body")}
        </Typography>
        {trialEndsAt ? (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: getMm(isDarkMode).onSurfaceVariant,
              mb: 3,
            }}
          >
            {t("membership_payment_required_trial_ended", {
              date: new Date(trialEndsAt).toLocaleDateString(),
            })}
          </Typography>
        ) : null}
        <Button
          onClick={handlePay}
          disabled={!paymentUrl}
          sx={primaryButtonSx}
          fullWidth
        >
          {t("membership_payment_required_cta")}
        </Button>
        {!paymentUrl ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 2,
              color: getMm(isDarkMode).error,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t("membership_payment_required_no_link")}
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
}
